/* eslint-disable no-debugger */

import React, {
  cloneElement,
  FC,
  ReactElement,
  useState,
  useEffect,
} from 'react';
import {
  List,
  TextField,
  ShowButton,
  DateField,
  TopToolbar,
  useListContext,
  CreateButton,
  sanitizeListRestProps,
  FunctionField,
  FunctionFieldProps,
  ToolbarProps,
  ExportButtonProps,
  ListProps,
  useDataProvider,
  useNotify,
} from 'react-admin';
import Datagrid from 'components/Datagrid';
import Box from '@material-ui/core/Box';
import CheckIcon from '@material-ui/icons/Check';
import PendingIcon from '@material-ui/icons/MoreHoriz';

import { exporter } from 'providers/export';
import { JobSheetFilters } from './filters/job-sheet-filters';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { UIProvider } from 'providers/uiProvider';
import { CAN_CHANGE_JOB } from 'providers/permissions';
import { useStyles } from './jobs-styles';
import { JobSheetInterface } from '@vatos-pas/common';
import CustomExportButton from 'components/CustomExportButton';
import { SupervisorField } from 'components/SupervisorListItems';
import InfoDialog from 'components/InfoDialog';
import Typography from '@material-ui/core/Typography/Typography';
import Chip from '@material-ui/core/Chip';
import { trackEvent } from 'utils/mixpanel';
import { MapProvider } from 'components/Map';
import AddressIntegrationField from '../components/address-integration-field';

export enum POStatus {
  NoPO = 'NoPO',
  Pending = 'Pending',
  Signed = 'Signed',
}

const StatusField: FC<Omit<FunctionFieldProps<JobSheetInterface>, 'render'>> =
  props => {
    const classes = useStyles();
    return (
      <FunctionField<JobSheetInterface>
        {...props}
        render={record => {
          switch (record?.poStatus) {
            case POStatus.Pending:
              return (
                <Box className={classes.pendingStatusBadge}>
                  <PendingIcon />
                </Box>
              );
            case POStatus.Signed:
              return (
                <Box className={classes.signedStatusBadge}>
                  <CheckIcon />
                </Box>
              );
            default:
              return null;
          }
        }}
      />
    );
  };

function haveSameValues(arr1, arr2) {
  if (arr1.length !== arr2.length) return false;

  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();

  return sorted1.every((value, index) => value === sorted2[index]);
}

type ListActionProps = ToolbarProps &
  Pick<ExportButtonProps, 'maxResults'> & {
    filters?: ReactElement;
  };

const ListActions: FC<ListActionProps> = ({
  className,
  filters,
  maxResults,
  ...rest
}) => {
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    basePath,
    showFilter,
    total,
  } = useListContext();

  const { hasPermission } = PermissionsProvider.useContainer();

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      {hasPermission(CAN_CHANGE_JOB) && <CreateButton basePath={basePath} />}
      <CustomExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
        maxResults={maxResults}
      />
    </TopToolbar>
  );
};

const PreRockField = props => {
  const preRockText = props.record.preRock === true ? 'Yes' : 'No';
  return <label>{preRockText}</label>;
};

const JobsListInner: FC<ListProps> = props => {
  const { opened } = UIProvider.useContainer();
  const dataProvider = useDataProvider();

  const [originalSupervisors, setOriginalSupervisors] = useState([]);
  const [currentSupervisors, setCurrentSupervisors] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const classes = useStyles();
  const notify = useNotify();
  console.log('');

  const onClickSupervisors = async record => {
    try {
      const response: any = await dataProvider.getList(
        'job-original-supervisor',
        {
          filter: { jobId: record.jobId },
          pagination: { page: 1, perPage: 900 },
          sort: { field: 'id', order: 'ASC' },
        },
      );
      const current = record.supervisorFullNames.split(',');
      setCurrentSupervisors(current);
      const mappedOriginal = response.data.map(
        item => item.user.firstName + ' ' + item.user.lastName,
      );
      setOriginalSupervisors(mappedOriginal);
      setIsModalOpen(true);
    } catch (err) {
      notify('Failed to get supevisor favorites', 'error');
    }
  };

  useEffect(() => {
    trackEvent('PageView', {
      resource: 'imported-jobs',
      page: window.location.pathname,
      role: localStorage.getItem('userRoles'),
      env: process.env.REACT_APP_MIXPANEL_ENV,
    });
  }, []);
  return (
    <>
      <List
        classes={{
          content: opened ? classes.content : classes.contentClosed,
          root: opened ? classes.root : classes.rootClosed,
        }}
        {...props}
        exporter={exporter('jobs')}
        bulkActionButtons={false}
        filterDefaultValues={{ 'voided||$eq': false }}
        filters={<JobSheetFilters>{props.filters}</JobSheetFilters>}
        actions={<ListActions maxResults={1000} />}
      >
        <Datagrid rowClick="show">
          <TextField source="builderName" label="Builder" />
          <TextField source="subdivisionName" label="Subdivision" />
          <TextField source="jobLot" label="Lot" />
          <TextField source="jobBlock" label="Block" />
          <TextField
            source="orderNumber"
            label="PO ID"
            emptyText="-"
            sortable={false}
          />
          <TextField source="modelName" label="Model" />
          <SupervisorField
            label="Supervisors"
            icon
            onClick={onClickSupervisors}
          />
          <TextField source="supplierName" label="Current Supplier" />
          <DateField source="createdAt" label="Created At" showTime />
          <StatusField label="Status" />
          <AddressIntegrationField
            label="Address Integration"
            sortable={false}
          />
          <DateField source="poSignedAt" label="Signed At" emptyText="-" />
          <PreRockField source="preRock" label="Pre-Rock" />
          <ShowButton />
        </Datagrid>
      </List>
      <InfoDialog
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        title="Subdivision Supervisors History"
        closeText="CLOSE"
        content={
          <>
            <Box width="100%" borderTop="1px solid black">
              <Box borderBottom="1px solid gray" pb={2} mt={2}>
                <Typography style={{ color: 'black' }}>
                  Current Supervisors
                </Typography>
                <Box mt={2}>
                  <Typography
                    style={{
                      fontSize: 12,
                      color: 'black',
                      marginBottom: '5px',
                    }}
                  >
                    Supervisors
                  </Typography>
                  <Box display="flex" flexWrap="wrap">
                    {currentSupervisors.map(item => (
                      <Chip
                        style={{ margin: '5px 0px 5px 5px' }}
                        key={item}
                        label={item}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
              {originalSupervisors.length > 0 &&
                !haveSameValues(originalSupervisors, currentSupervisors) && (
                  <Box mt={2}>
                    <Typography style={{ color: 'black' }}>
                      Original Supervisors
                    </Typography>
                    <Box mt={2}>
                      <Typography
                        style={{
                          fontSize: 12,
                          color: 'black',
                          marginBottom: '5px',
                        }}
                      >
                        Supervisors
                      </Typography>
                      <Box display="flex" flexWrap="wrap">
                        {originalSupervisors.map((item: any) => (
                          <Chip
                            style={{ margin: '5px 0px 5px 5px' }}
                            key={item}
                            label={item}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
            </Box>
          </>
        }
      />
    </>
  );
};

export const JobsList: FC<ListProps> = props => (
  <MapProvider>
    <JobsListInner {...props} />
  </MapProvider>
);
